import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { site } from '../../data/site';
import { Container } from '../ui/Container';
const testimonials = [{
  "id": "claudia",
  "name": "Claudia Valles-Priest",
  "theme": "Coaching that makes a difference",
  "quote": "…really make a difference…",
  "summary": "Claudia credits the instructors’ dedication with her son’s athletic progress and enthusiasm for Taekwondo. She also describes his strong connection with his coach and teammates."
}, {
  "id": "jason",
  "name": "Jason Hammond",
  "theme": "Train together. Grow together.",
  "quote": "Great gym for any age.",
  "summary": "Jason values learning alongside his son: shared training gives them time together while developing useful life skills, with coaching that balances patience and discipline."
}, {
  "id": "tighe",
  "name": "Tighe Burg",
  "theme": "Skills that go beyond the mat",
  "quote": "…leadership and family fun.",
  "summary": "After visiting many schools, Tighe highlights Chee’s instruction in concentration, effort and technique, alongside the broader qualities that make training meaningful for families."
}, {
  "id": "wenona",
  "name": "Wenona Ayarbe",
  "theme": "A place to belong",
  "quote": "The family atmosphere.",
  "summary": "Wenona describes a welcoming school where beginners and committed competitors receive attentive instruction, and where parents can take part in training too."
}, {
  "id": "kerrie",
  "name": "Kerrie Kiefer",
  "theme": "Personal attention. Lasting growth.",
  "quote": "They have matured and love going to class.",
  "summary": "Kerrie saw positive changes at home after enrolling her three sons. She values the way instruction adapts to each child’s level, even within a shared class."
}];
const ROTATION_MS = 9000;
export function ParentFeedback() {
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  useEffect(() => {
    if (reduceMotion) setIsPlaying(false);
  }, [reduceMotion]);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25
    });
    observer.observe(section);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);
  useEffect(() => {
    if (!isPlaying || hovered || !inView || !pageVisible) return;
    const timer = window.setInterval(() => {
      setCurrent(index => (index + 1) % testimonials.length);
    }, ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [isPlaying, hovered, inView, pageVisible, reduceMotion, current]);
  const show = (index: number) => {
    setCurrent((index + testimonials.length) % testimonials.length);
    setIsPlaying(false);
  };
  return <section ref={sectionRef} id="families" aria-label="Bates community testimonials" aria-roledescription="carousel" className="bg-paper py-16 lg:py-24" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocusCapture={event => {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-rotation-control]') && !event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsPlaying(false);
    }
  }} onKeyDown={event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      show(current + (event.key === 'ArrowRight' ? 1 : -1));
    }
  }} data-id="element-167" data-mp="10fp8wvr1en1h">
      <Container data-id="element-168" data-mp="u19x484unwem">
        <div className="grid gap-7 lg:grid-cols-12 lg:gap-10" data-id="element-169" data-mp="yxql92c1uarp">
          <div className="lg:col-span-3" data-id="element-170" data-mp="yy8pal4ydta6">
            <p className="font-display text-lg font-semibold uppercase tracking-[0.12em] text-muted" data-id="element-171" data-mp="1mn1dfvfc2wu8">
              From the Bates community
            </p>
            <p className="mt-3 max-w-[24rem] text-sm leading-relaxed text-muted" data-id="element-172" data-mp="1rlw9hz2bahwa">
              Five perspectives on training, coaching and belonging at Bates.
            </p>
            <a href={site.testimonialsUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4 hover:text-brand" data-id="element-173" data-mp="1hvjne2eumgo1">
              Read full testimonials
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-174" data-mp="3v1zu0pis89t9" />
              <span className="sr-only" data-id="element-175" data-mp="3q59afs7rqw33"> (opens in a new tab)</span>
            </a>
          </div>
          <div className="min-w-0 lg:col-span-9" data-id="element-176" data-mp="8o9dt5epabtb">
            <div className="grid" aria-live={isPlaying ? 'off' : 'polite'} aria-atomic="true" data-id="element-177" data-mp="8q2nyuoive62">
              {testimonials.map((testimonial, index) => <figure key={testimonial.id} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${testimonials.length}: ${testimonial.name}`} aria-hidden={index !== current} className={`col-start-1 row-start-1 flex min-w-0 flex-col transition-opacity duration-500 motion-reduce:transition-none ${index === current ? 'opacity-100' : 'pointer-events-none select-none opacity-0'}`} data-id="element-178" data-mp="3c5rix53394q1" data-mpi={index}>
                  <p className="font-display text-sm font-semibold uppercase tracking-[0.13em] text-brand" data-id="element-179" data-mp="wdaqj4ukg94e">
                    {testimonial.theme}
                  </p>
                  <blockquote className="mt-4 text-balance text-[1.75rem] font-medium leading-[1.25] tracking-[-0.015em] sm:text-[2.25rem] lg:text-[2.7rem]" data-id="element-180" data-mp="3u62q4il6kyjb">
                    <p data-id="element-181" data-mp="72fqi92k22ex">“{testimonial.quote}”</p>
                  </blockquote>
                  <p className="mt-5 max-w-[48rem] text-base leading-relaxed text-muted sm:text-lg" data-id="element-182" data-mp="70bpbyw1jhic">
                    {testimonial.summary}
                  </p>
                  <figcaption className="mt-auto pt-6" data-id="element-183" data-mp="3bhp93i4ho68s">
                    <span className="block font-semibold" data-id="element-184" data-mp="3fi9a4ws6nu07">{testimonial.name}</span>
                    <span className="mt-1 block text-xs text-muted" data-id="element-185" data-mp="3fgem39lm7lrw">Excerpt and summary from the school’s testimonials</span>
                  </figcaption>
                </figure>)}
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-186" data-mp="2rsn9e4w5x6q3">
              <div className="flex items-center gap-1" role="group" aria-label="Choose a testimonial" data-id="element-187" data-mp="2k8vvkrqubni1">
                <button type="button" onClick={() => show(current - 1)} aria-label="Previous testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-line transition-colors hover:border-brand hover:text-brand" data-id="element-188" data-mp="2f9jb2dnfysim">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-189" data-mp="26bpfx61ey4x1" />
                </button>
                {testimonials.map((testimonial, index) => <button key={testimonial.id} type="button" onClick={() => show(index)} aria-label={`Show testimonial ${index + 1} of ${testimonials.length}: ${testimonial.name}`} aria-current={index === current ? 'true' : undefined} className="flex h-11 w-8 items-center justify-center rounded-full" data-id="element-190" data-mp="1wirsclhkdprk" data-mpi={index}>
                    <span className={`h-2 rounded-full transition-all motion-reduce:transition-none ${index === current ? 'w-5 bg-brand' : 'w-2 bg-muted/35'}`} data-id="element-191" data-mp="1dkz3uo7otidp" />
                  </button>)}
                <button type="button" onClick={() => show(current + 1)} aria-label="Next testimonial" className="flex h-11 w-11 items-center justify-center rounded-full border border-line transition-colors hover:border-brand hover:text-brand" data-id="element-192" data-mp="36h00dfjoij7e">
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-193" data-mp="2xhwey4x1hrpq" />
                </button>
              </div>
              <div className="flex items-center gap-4" data-id="element-194" data-mp="2nhm24lex2mb3">
                <span className="text-xs tabular-nums text-muted" aria-hidden="true" data-id="element-195" data-mp="2nj6uq5ho7v6b">{current + 1} / {testimonials.length}</span>
                <button type="button" data-rotation-control onClick={() => setIsPlaying(playing => !playing)} aria-label={isPlaying ? 'Pause testimonial rotation' : 'Play testimonial rotation'} className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-ink hover:text-brand" data-id="element-196" data-mp="29oeawuuqzstx">
                  {isPlaying ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-197" data-mp="1l6duq55j1yd5" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-198" data-mp="1g69dgcwsozlv" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}